import apiClient from './api-client.js';
import { getApiToken } from './tokenUtils.js';
import { getJwtToken } from './jwtTokenManager.js';

const userId = () => String(document.querySelector('meta[name="user-id"]')?.content || '');
const identity = () => `${userId()}:${getJwtToken()?.account?.userId || ''}`;
let revision = 0;
let queued = false;
let requests = Promise.resolve();

function deviceTimezone() {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
    } catch {
        return null;
    }
}

function publish(state) {
    const timezone = state.timezone_mode === 'automatic' ? deviceTimezone() || state.tz : state.tz;
    refreshGreeting(timezone, state);
    window.dispatchEvent(new CustomEvent('timezone-change', { detail: { ...state, timezone } }));
}

function writeCookie(timezone) {
    document.cookie = `device_timezone=${encodeURIComponent(timezone)}; Path=/; SameSite=Lax${location.protocol === 'https:' ? '; Secure' : ''}`;
}

async function report(expectedUserId, timezone, generation) {
    if (expectedUserId !== userId() || generation !== revision || !await getApiToken()) return;
    if (expectedUserId !== userId() || generation !== revision) return;
    const startedIdentity = identity();
    const response = await apiClient.request('/timezone-sync', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({ timezone, expected_user_id: expectedUserId }).toString()
    });
    if (!response.ok) return;
    const state = await response.json();
    if (startedIdentity === identity() && expectedUserId === userId() && generation === revision) publish(state);
}

function reconcile() {
    const timezone = deviceTimezone();
    if (timezone) writeCookie(timezone);
    refreshGreeting(timezone);
    if (!timezone || !userId() || queued) return;
    queued = true;
    const expectedUserId = userId();
    const generation = revision;
    // Initial load and its pageshow share one activation. Later resumes always report.
    queueMicrotask(() => {
        queued = false;
        requests = requests.catch(() => {}).then(() => report(expectedUserId, timezone, generation)).catch(() => {});
    });
}

function update(mode, timezone) {
    const expectedUserId = userId();
    const generation = ++revision;
    // A response fence cannot undo an older report that commits after an explicit update.
    const operation = requests.catch(() => {}).then(() => writePreference(expectedUserId, generation, mode, timezone));
    requests = operation.catch(() => {});
    return operation;
}

async function writePreference(expectedUserId, generation, mode, timezone) {
    if (!expectedUserId || !await getApiToken()) throw new Error('Timezone authentication failed');
    if (generation !== revision || expectedUserId !== userId()
        || String(getJwtToken()?.account?.userId || '') !== expectedUserId) {
        throw new Error('Timezone account changed');
    }
    const startedIdentity = identity();
    const response = await apiClient.request(`/settings/${encodeURIComponent(expectedUserId)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ timezone_mode: mode, tz: mode === 'automatic' ? deviceTimezone() || timezone : timezone })
    });
    if (!response.ok) throw new Error('Timezone update failed');
    const state = await response.json();
    if (generation !== revision || expectedUserId !== userId() || startedIdentity !== identity()) {
        throw new Error('Timezone account changed');
    }
    publish(state);
    return state;
}

window.TimezonePreference = { reconcile, update };
window.addEventListener('storage', event => {
    if (event.key === 'redux-state') {
        revision++;
        const heading = document.querySelector('[data-timezone-greeting]');
        if (heading) heading.dataset.timezoneMode = '';
    }
});
window.addEventListener('pageshow', reconcile);
document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') reconcile();
});
function refreshGreeting(deviceZone, state = null) {
    const heading = document.querySelector('[data-timezone-greeting]');
    if (!heading) return;
    const tokenUserId = String(getJwtToken()?.account?.userId || '');
    if (!userId() || heading.dataset.userId !== userId() || (tokenUserId && tokenUserId !== userId())) {
        heading.dataset.timezoneMode = '';
        return;
    }
    if (state) {
        heading.dataset.timezoneMode = state.timezone_mode;
        heading.dataset.timezone = state.tz;
    }
    const mode = heading.dataset.timezoneMode;
    if (mode !== 'automatic' && mode !== 'manual') return;
    const timezone = mode === 'automatic' ? deviceZone || heading.dataset.timezone : heading.dataset.timezone;
    try {
        const hour = Number(new Intl.DateTimeFormat('en-US', {
            hour: 'numeric', hourCycle: 'h23', timeZone: timezone || 'UTC'
        }).format(new Date()));
        const part = hour >= 5 && hour < 12 ? 'morning' : hour >= 12 && hour < 18 ? 'afternoon' : 'evening';
        heading.textContent = `Good ${part}, ${heading.dataset.username}.`;
    } catch {
        // A later valid report can refresh the greeting.
    }
}
// Deferred bundles normally precede pageshow; late bundles need their own activation.
if (document.readyState === 'complete') reconcile();
else {
    const timezone = deviceTimezone();
    if (timezone) writeCookie(timezone);
    refreshGreeting(timezone);
}
